// Gym catch-up reminder widget (CATCHUP_WIDGETS_PLAN.md Task 8, D-CU-15). Shows
// the weekly logged-vs-planned shortfall and navigates to the Trainer page --
// no in-widget backfill write in v1, since a gym_exercise needs real set/rep/
// equipment detail the Trainer page collects (full backfill deferred).
// PtReminderWidget-style reminder card: self-fetching, null when absent/off.

import { useEffect, useState } from "react";
import type { CatchUpResponse } from "@medassist/shared";
import { resolveCatchUpWidgets, resolveProactiveSettings } from "@medassist/shared";
import { getCatchUpData } from "../../../lib/catchUpFetch.js";
import { ApiError } from "../../../lib/api.js";
import { BarbellIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

export function GymCatchUpWidget({ patient, onNavigate, onError }: WidgetProps) {
  const [response, setResponse] = useState<CatchUpResponse | null>(null);

  const enabled = resolveCatchUpWidgets(
    resolveProactiveSettings(patient.proactiveSuggestions).catchUpWidgets
  ).gym;

  useEffect(() => {
    if (!enabled) return;
    getCatchUpData(patient.patientId)
      .then(setResponse)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load gym catch-up"));
  }, [patient.patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const section = response?.sections.find((s) => s.kind === "gym");
  if (!section) return null;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <BarbellIcon size={16} className="text-amber-500 dark:text-amber-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Gym catch-up</h2>
        <button onClick={() => onNavigate("trainer")} className="ml-auto text-xs text-(--accent) hover:underline">
          Go to Trainer
        </button>
      </div>
      <p className="text-sm text-amber-700 dark:text-amber-300">
        You logged {section.loggedDaysLastWeek} of {section.plannedDaysPerWeek} planned gym days this week
      </p>
      <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">{section.programTitle}</p>
    </section>
  );
}
